import defaultTranslations from '../ConsentManager.json';

/**
 * ConsentManager.Translator
 *
 * @author Felix Kiesewetter <info@felixkiesewetter.de>
 * @package felixkiesewetter-js/consentmanager
 * @lastModified 2024-12-18 Removed translations file option because it has no functionality, TypeScript.
 * @since 5.1.0
 */
class Translator
{

    #language :string = 'de';
    #translations :object = {};

    /**
     * Translation service constructor
     *
     * @param {object} translations
     * @param {null|string} language
     */
    public constructor(
        translations :object = {},
        language :null|string = null
    )
    {
        this.setLanguage(language);
        this.setTranslations(defaultTranslations);
        this.mergeTranslations(translations);
    }

    // #######
    // Methods
    // #######

    /**
     * Merge translations from translations file with incoming translations
     *
     * @param translations
     */
    public mergeTranslations(translations :object = {}) :Translator
    {
        if (!Object.values(translations).length) {
            return this;
        }
        if (Object.values(translations)[0].constructor === Object) {
            Object.keys(translations).forEach(language => {
                if (this.#language !== language) {
                    return
                }
                translations = translations[language]
            })
        }
        const defaultTranslations = DefaultTranslations[this.#language];
        translations = {...defaultTranslations, ...translations};
        Object.keys(translations).forEach((key) => {
            this.set(key, translations[key])
        });
        return this;
    }

    /**
     * Replace all translatable keys within a string
     *
     * @param {string} string
     * @returns {string}
     */
    public replaceAll(string :string) :string
    {
        if (!this.#translations[this.#language]) {
            return string;
        }
        Object.keys(this.#translations[this.#language]).forEach((key) => {
            const value = this.#translations[this.#language][key];
            string = string.replace(`{${key}}`, value);
        })
        return string;
    }

    // #######
    // Getters
    // #######

    /**
     * Get a translation
     *
     * @param {string} key
     * @returns {string}
     */
    public get(key='') :string
    {
        return this.#translations[this.#language]?.[key] ?? '';
    }

    /**
     * Get all translations from current language
     *
     * @returns {object}
     */
    public getAll() :object
    {
        return this.#translations[this.#language];
    }

    /**
     * Get current language
     *
     * @returns {string}
     */
    public getLanguage() :string
    {
        return this.#language;
    }

    /**
     * Private getter for the translations object
     *
     * @private
     */
    private getTranslations() :object
    {
        return this.#translations;
    }

    // #######
    // Setters
    // #######

    /**
     * Set a translation for given key
     *
     * @param {string} key
     * @param {string} value
     * @returns {Translator}
     */
    set(key :string, value :string) :Translator
    {
        if (!this.#translations[this.#language]) {
            this.#translations[this.#language] = {};
        }
        this.#translations[this.#language][key] = value;
        return this;
    }

    /**
     * Set current language
     *
     * @param {null|string} language
     * @returns {Translator}
     */
    setLanguage(language :null|string = null) :Translator
    {
        this.#language = language || (document.documentElement.getAttribute('lang') || 'en');
        if (2 !== this.#language.length) {
            this.#language = this.#language.substring(0, 2).toLowerCase();
        }
        return this;
    }

    /**
     * Setter for the translations object
     *
     * @param translations
     */
    setTranslations(translations :object) :Translator
    {
        this.#translations = translations;
        return this;
    }

}

export default Translator;